import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import knowledgeBaseService from '../api/knowledgeBaseService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SensitiveContentWrapper, ReadOnlyIndicator } from '../components/ui/PermissionWrapper';
import { usePermissions } from '../hooks/usePermissions';

const KnowledgeBase = () => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  
  // Check if user has manage access vs read-only access
  const canManageKnowledgeBase = permissions.canManage('knowledgeBase');
  const canViewKnowledgeBase = permissions.canView('knowledgeBase');
  const hasReadOnlyAccess = canViewKnowledgeBase && !canManageKnowledgeBase;
  
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('text'); // 'text', 'file', 'website', or 'api'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [scrapeStatus, setScrapeStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [newlyAddedDocId, setNewlyAddedDocId] = useState(null);
  const [isPineconeConfigOpen, setIsPineconeConfigOpen] = useState(false);
  
  // API scraping state
  const [apiConfig, setApiConfig] = useState({
    url: '',
    method: 'GET',
    headers: {},
    body: ''
  });
  const [apiResponse, setApiResponse] = useState(null);
  const [apiTesting, setApiTesting] = useState(false);
  const [selectedFields, setSelectedFields] = useState({});
  const [fieldMappings, setFieldMappings] = useState({});
  const [useTemplateMode, setUseTemplateMode] = useState(false);
  const [contentTemplate, setContentTemplate] = useState('');
  const [isRescraping, setIsRescraping] = useState(false);
  
  const newDocRef = useRef(null);

  // Fetch project details including Pinecone config
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => knowledgeBaseService.getProject(projectId),
  });

  // Fetch documents
  const { data: documents, isLoading } = useQuery({
    queryKey: ['knowledgeBase', projectId],
    queryFn: () => knowledgeBaseService.getDocuments(projectId),
  });

  // Scroll to newly added document
  useEffect(() => {
    if (newlyAddedDocId && newDocRef.current) {
      newDocRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight effect with timeout
      setTimeout(() => {
        setNewlyAddedDocId(null);
      }, 5000); // Clear highlight after 5 seconds
    }
  }, [newlyAddedDocId, documents]);

  // Reset form function
  const resetForm = () => {
    setIsFormOpen(false);
    setSelectedDocument(null);
    setActiveTab('text');
    setUploadProgress(0);
    setIsUploading(false);
    setScrapeStatus('idle');
    // Reset API scraping state
    setApiConfig({
      url: '',
      method: 'GET',
      headers: {},
      body: ''
    });
    setApiResponse(null);
    setApiTesting(false);
    setSelectedFields({});
    setFieldMappings({});
    setUseTemplateMode(false);
    setContentTemplate('');
    setIsRescraping(false);
  };

  // Check if document is from API scraping
  const isApiDocument = (document) => {
    return document?.source?.startsWith('API:') || 
           (document?.metadata && (document.metadata.get('apiUrl') || document.metadata.apiUrl));
  };

  // Load API document data for editing/re-scraping
  const loadApiDocumentData = (document) => {
    if (!isApiDocument(document)) return;

    const metadata = document.metadata;
    if (metadata) {
      const apiUrl = metadata.get ? metadata.get('apiUrl') : metadata.apiUrl;
      const apiMethod = metadata.get ? metadata.get('apiMethod') : metadata.apiMethod;
      const selectedFieldsStr = metadata.get ? metadata.get('selectedFields') : metadata.selectedFields;
      const fieldMappingsStr = metadata.get ? metadata.get('fieldMappings') : metadata.fieldMappings;
      const template = metadata.get ? metadata.get('contentTemplate') : metadata.contentTemplate;
      const headersStr = metadata.get ? metadata.get('apiHeaders') : metadata.apiHeaders;
      const bodyStr = metadata.get ? metadata.get('apiBody') : metadata.apiBody;

      if (apiUrl) {
        setApiConfig({
          url: apiUrl,
          method: apiMethod || 'GET',
          headers: headersStr ? JSON.parse(headersStr) : {},
          body: bodyStr || ''
        });

        if (template) {
          setUseTemplateMode(true);
          setContentTemplate(template);
        } else if (selectedFieldsStr) {
          try {
            setSelectedFields(JSON.parse(selectedFieldsStr).reduce((acc, field) => {
              acc[field] = true;
              return acc;
            }, {}));
          } catch (e) {
            console.error('Error parsing selected fields:', e);
          }

          if (fieldMappingsStr) {
            try {
              setFieldMappings(JSON.parse(fieldMappingsStr));
            } catch (e) {
              console.error('Error parsing field mappings:', e);
            }
          }
        }
      }
    }
  };

  // Add/Update document mutation
  const { mutate: saveDocument } = useMutation({
    mutationFn: (data) => {
      if (selectedDocument) {
        return knowledgeBaseService.updateDocument(projectId, selectedDocument._id, data);
      }
      return knowledgeBaseService.addTextDocument(projectId, data);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries(['knowledgeBase', projectId]);
      
      // Set newly added document ID to highlight it
      if (!selectedDocument) {
        setNewlyAddedDocId(response._id);
        toast.success('Document successfully created!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        toast.info('Document successfully updated', {
          position: "top-right",
          autoClose: 3000,
        });
      }
      
      resetForm();
    },
    onError: (error) => {
      toast.error(`Error: ${error.message || 'Failed to save document'}`, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  });

  // Delete document mutation
  const { mutate: deleteDocument } = useMutation({
    mutationFn: (documentId) => knowledgeBaseService.deleteDocument(projectId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries(['knowledgeBase', projectId]);
      toast.info('Document deleted', {
        position: "top-right",
        autoClose: 3000,
      });
    },
    onError: (error) => {
      toast.error(`Error deleting document: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  });

  // File upload mutation
  const { mutate: uploadFile } = useMutation({
    mutationFn: (formData) => {
      setIsUploading(true);
      setUploadProgress(0);
      
      // Pass the progress update function as the third parameter
      return knowledgeBaseService.uploadFile(
        projectId, 
        formData,
        (progress) => {
          // Update the progress state
          setUploadProgress(progress);
          console.log(`Upload progress updated: ${progress}%`);
        }
      );
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries(['knowledgeBase', projectId]);
      setNewlyAddedDocId(response._id);
      setIsUploading(false);
      setUploadProgress(100);
      
      toast.success('File successfully uploaded and processed!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      resetForm();
    },
    onError: (error) => {
      setIsUploading(false);
      setUploadProgress(0);
      toast.error(`Error uploading file: ${error.message || 'Upload failed'}`, {
        position: "top-right",
        autoClose: 5000,
      });
    },
  });

  // Website scraping mutation
  const { mutate: scrapeWebsite } = useMutation({
    mutationFn: (data) => {
      setScrapeStatus('loading');
      return knowledgeBaseService.scrapeWebsite(projectId, data);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries(['knowledgeBase', projectId]);
      setNewlyAddedDocId(response._id);
      setScrapeStatus('success');
      
      toast.success('Website content successfully scraped and added!', {
        position: "top-right",
        autoClose: 3000,
      });
      
      resetForm();
    },
    onError: (error) => {
      setScrapeStatus('error');
      toast.error(`Error scraping website: ${error.message || 'Failed to scrape content'}`, {
        position: "top-right",
        autoClose: 5000,
      });
    },
  });

  // Pinecone configuration mutation
  const { mutate: updatePineconeConfig } = useMutation({
    mutationFn: (config) => knowledgeBaseService.updatePineconeConfig(projectId, config),
    onSuccess: () => {
      queryClient.invalidateQueries(['project', projectId]);
      toast.success('Pinecone configuration updated successfully');
      setIsPineconeConfigOpen(false);
    },
    onError: (error) => {
      toast.error(`Error updating Pinecone configuration: ${error.message}`);
    }
  });

  // Test API endpoint mutation
  const { mutate: testApiEndpoint } = useMutation({
    mutationFn: (config) => knowledgeBaseService.testApiEndpoint(projectId, config),
    onSuccess: (response) => {
      setApiResponse(response);
      setApiTesting(false);
      
      if (useTemplateMode && response.formattedPreview) {
        toast.success('API test successful! Check the formatted preview below.');
      } else {
        toast.success('API test successful! Select fields to store or switch to template mode.');
      }
    },
    onError: (error) => {
      setApiTesting(false);
      toast.error(`API test failed: ${error.message}`);
    }
  });

  // Scrape API mutation
  const { mutate: scrapeApi } = useMutation({
    mutationFn: (data) => knowledgeBaseService.scrapeApi(projectId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['knowledgeBase', projectId]);
      setNewlyAddedDocId(response._id);
      toast.success('API data successfully imported!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      resetForm();
    },
    onError: (error) => {
      toast.error(`Error importing API data: ${error.message || 'Failed to import API data'}`, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  });

  // Re-scrape API mutation
  const { mutate: rescrapeApi } = useMutation({
    mutationFn: (data) => {
      setIsRescraping(true);
      return knowledgeBaseService.rescrapeApi(projectId, selectedDocument._id, data);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries(['knowledgeBase', projectId]);
      setIsRescraping(false);
      toast.success('API data successfully re-scraped and updated!', {
        position: "top-right",
        autoClose: 3000,
      });
      resetForm();
    },
    onError: (error) => {
      setIsRescraping(false);
      toast.error(`Error re-scraping API data: ${error.message || 'Failed to re-scrape data'}`, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  });

  // API testing and helper functions
  const handleTestApi = () => {
    if (!apiConfig.url) {
      toast.error('Please enter an API URL');
      return;
    }
    setApiTesting(true);
    
    // Include template in test request if in template mode
    const testConfig = {
      ...apiConfig,
      template: useTemplateMode ? contentTemplate : null
    };
    
    testApiEndpoint(testConfig);
  };

  const handleRescrape = () => {
    if (!selectedDocument || !isApiDocument(selectedDocument)) {
      toast.error('This document is not from API scraping');
      return;
    }

    if (!apiConfig.url) {
      toast.error('Please configure the API settings first');
      return;
    }

    const data = {
      apiConfig,
      useTemplateMode,
      contentTemplate: useTemplateMode ? contentTemplate : '',
      selectedFields: useTemplateMode ? [] : Object.keys(selectedFields).filter(path => selectedFields[path]),
      fieldMappings: useTemplateMode ? {} : fieldMappings,
    };

    rescrapeApi(data);
  };

  const handleFieldSelection = (path, selected) => {
    setSelectedFields(prev => ({
      ...prev,
      [path]: selected
    }));
  };

  const handleFieldMapping = (path, mapping) => {
    setFieldMappings(prev => ({
      ...prev,
      [path]: mapping
    }));
  };

  const renderJsonStructure = (obj, path = '', level = 0) => {
    if (!obj || typeof obj !== 'object') return null;
    
    return Object.entries(obj).map(([key, value]) => {
      const currentPath = path ? `${path}.${key}` : key;
      const isSelected = selectedFields[currentPath];
      
      return (
        <div key={currentPath} style={{ marginLeft: level * 20 }}>
          <div className="flex items-center space-x-2 py-1">
            <input
              type="checkbox"
              checked={isSelected || false}
              onChange={(e) => handleFieldSelection(currentPath, e.target.checked)}
              className="form-checkbox"
            />
            <span className="font-mono text-sm">
              {key}: {typeof value === 'object' ? '{...}' : typeof value}
            </span>
            {isSelected && (
              <input
                type="text"
                placeholder="Field name in document"
                value={fieldMappings[currentPath] || key}
                onChange={(e) => handleFieldMapping(currentPath, e.target.value)}
                className="form-input form-input-sm"
                style={{ width: '200px' }}
              />
            )}
          </div>
          {typeof value === 'object' && value !== null && level < 3 && 
            renderJsonStructure(value, currentPath, level + 1)
          }
        </div>
      );
    });
  };

  const generateTemplatePreview = () => {
    if (!contentTemplate) return 'Enter a template to see preview...';
    if (!apiResponse) return 'Test your API first to see the formatted preview...';

    try {
      // Use server-processed preview if available (more accurate)
      if (apiResponse.formattedPreview) {
        return apiResponse.formattedPreview;
      }
      
      // Fallback to client-side preview
      return knowledgeBaseService.previewTemplate(contentTemplate, apiResponse);
    } catch (error) {
      return `Template preview error: ${error.message}`;
    }
  };

  const handleDocumentSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    if (activeTab === 'text') {
      const data = {
        title: formData.get('title'),
        content: formData.get('content'),
        tags: formData.get('tags').split(',').map(tag => tag.trim()).filter(Boolean),
        projectId
      };
      saveDocument(data);
    } else if (activeTab === 'file') {
      const file = formData.get('file');
      if (!file) return;
      
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('title', formData.get('title'));
      uploadFormData.append('tags', formData.get('tags'));
      uploadFormData.append('projectId', projectId);
      
      uploadFile(uploadFormData);
    } else if (activeTab === 'website') {
      const data = {
        url: formData.get('url'),
        title: formData.get('title'),
        tags: formData.get('tags').split(',').map(tag => tag.trim()).filter(Boolean),
        projectId
      };
      scrapeWebsite(data);
    } else if (activeTab === 'api') {
      if (!apiResponse) {
        toast.error('Please test the API first to see the response structure');
        return;
      }
      
      if (useTemplateMode) {
        if (!contentTemplate.trim()) {
          toast.error('Please provide a content template');
          return;
        }
      } else {
        const selectedFieldPaths = Object.keys(selectedFields).filter(path => selectedFields[path]);
        if (selectedFieldPaths.length === 0) {
          toast.error('Please select at least one field to store');
          return;
        }
      }
      
      const data = {
        title: formData.get('title'),
        apiConfig,
        useTemplateMode,
        contentTemplate: useTemplateMode ? contentTemplate : '',
        selectedFields: useTemplateMode ? [] : Object.keys(selectedFields).filter(path => selectedFields[path]),
        fieldMappings: useTemplateMode ? {} : fieldMappings,
        tags: formData.get('tags').split(',').map(tag => tag.trim()).filter(Boolean),
        projectId
      };
      scrapeApi(data);
    }
  };

  const handlePineconeConfigSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const config = {
      apiKey: formData.get('apiKey'),
      indexName: formData.get('indexName'),
      cloud: formData.get('cloud'),
      region: formData.get('region'),
      dimension: parseInt(formData.get('dimension')),
      metric: formData.get('metric')
    };
    updatePineconeConfig(config);
  };

  return (
    <Layout>
      <div className="dashboard">
        <div className="dashboard-header">
          <h1 className="page-title">Knowledge Base</h1>
          <div className="space-x-2">
            {canManageKnowledgeBase && (
              <button
                className="btn btn-primary text-smd"
                onClick={() => {
                  setSelectedDocument(null);
                  setIsFormOpen(true);
                  setActiveTab('text');
                }}
              >
                Add Document
              </button>
            )}
            <SensitiveContentWrapper>
              <button
                className="btn btn-secondary text-smd ml-2"
                onClick={() => setIsPineconeConfigOpen(true)}
              >
                Configure Pinecone
              </button>
            </SensitiveContentWrapper>
          </div>
        </div>

        <SensitiveContentWrapper>
          {isPineconeConfigOpen && (
            <div className="card mt-4 p-4">
              <h2 className="text-lg font-medium mb-4">Pinecone Configuration</h2>
              <form onSubmit={handlePineconeConfigSubmit}>
              <div className="form-group">
                <label className="form-label">API Key</label>
                <input
                  type="password"
                  name="apiKey"
                  className="form-input"
                  defaultValue={project?.pineconeConfig?.apiKey}
                  required
                  placeholder="Enter your Pinecone API key"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Index Name</label>
                <input
                  type="text"
                  name="indexName"
                  className="form-input"
                  defaultValue={project?.pineconeConfig?.indexName}
                  required
                  placeholder="Enter your Pinecone index name"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Cloud Provider</label>
                <select
                  name="cloud"
                  className="form-input"
                  defaultValue={project?.pineconeConfig?.cloud}
                  required
                >
                  <option value="aws">AWS</option>
                  <option value="gcp">Google Cloud</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Region</label>
                <select
                  name="region"
                  className="form-input"
                  defaultValue={project?.pineconeConfig?.region}
                  required
                >
                  <option value="us-east-1">US East (N. Virginia)</option>
                  <option value="us-west-2">US West (Oregon)</option>
                  <option value="eu-west-1">EU (Ireland)</option>
                  <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Dimension</label>
                <select
                  name="dimension"
                  className="form-input"
                  defaultValue={project?.pineconeConfig?.dimension}
                  required
                >
                  <option value="1024">1024 (Recommended)</option>
                  <option value="1536">1536</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Similarity Metric</label>
                <select
                  name="metric"
                  className="form-input"
                  defaultValue={project?.pineconeConfig?.metric}
                  required
                >
                  <option value="cosine">Cosine Similarity</option>
                  <option value="euclidean">Euclidean Distance</option>
                  <option value="dotproduct">Dot Product</option>
                </select>
              </div>

              <div className="flex gap-2">
                <button type="submit" className="btn btn-primary text-smd">
                  Save Configuration
                </button>
                <button
                  type="button"
                  className="btn btn-secondary text-smd ml-2"
                  onClick={() => setIsPineconeConfigOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
        </SensitiveContentWrapper>

        {isFormOpen && (
          <div className="card mt-4 p-4">
            <h2 className="text-lg font-medium mb-4">
              {selectedDocument ? 'Edit Document' : 'New Document'}
            </h2>
            
            {hasReadOnlyAccess && (
              <ReadOnlyIndicator />
            )}
            
            {!selectedDocument && (
              <div className="mb-4">
                <div className="flex border-b">
                  <button
                    className={`px-4 py-2 text-smd ${activeTab === 'text' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500'}`}
                    onClick={() => setActiveTab('text')}
                  >
                    Text Input
                  </button>
                  <button
                    className={`px-4 py-2 text-smd ${activeTab === 'file' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500'}`}
                    onClick={() => setActiveTab('file')}
                  >
                    File Upload
                  </button>
                  <button
                    className={`px-4 py-2 text-smd ${activeTab === 'website' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500'}`}
                    onClick={() => setActiveTab('website')}
                  >
                    Website Scraping
                  </button>
                  <button
                    className={`px-4 py-2 text-smd ${activeTab === 'api' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500'}`}
                    onClick={() => setActiveTab('api')}
                  >
                    API Scraping
                  </button>
                </div>
              </div>
            )}
            
            <form onSubmit={handleDocumentSubmit}>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  name="title"
                  className="form-input"
                  defaultValue={selectedDocument?.title}
                  required
                  disabled={hasReadOnlyAccess}
                />
              </div>

              {(activeTab === 'text' || selectedDocument) && (
                <div className="form-group">
                  <label className="form-label">Content</label>
                  <textarea
                    name="content"
                    className="form-textarea h-250 text-md w-full"
                    defaultValue={selectedDocument?.content}
                    required={activeTab === 'text'}
                    placeholder="Enter document content..."
                    disabled={hasReadOnlyAccess}
                  />
                </div>
              )}

              {activeTab === 'file' && !selectedDocument && (
                <div className="form-group">
                  <label className="form-label">File</label>
                  <input
                    type="file"
                    name="file"
                    className="form-input"
                    required
                    accept=".pdf,.doc,.docx,.txt,.md,.csv,.json"
                    disabled={hasReadOnlyAccess}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Supported formats: PDF, Word, Text, Markdown, CSV, JSON
                  </p>
                  
                  {isUploading && (
                    <div className="mt-3 mb-1">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-blue-700">Uploading...</span>
                        <span className="text-sm font-medium text-blue-700">{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out" 
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {uploadProgress < 100 
                          ? "Your file is being uploaded. Please wait..." 
                          : "Processing the document..."}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'website' && !selectedDocument && (
                <div className="form-group">
                  <label className="form-label">Website URL</label>
                  <input
                    type="url"
                    name="url"
                    className="form-input"
                    required
                    placeholder="https://example.com/page-to-scrape"
                    disabled={hasReadOnlyAccess}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Enter the URL of the webpage you want to scrape for content
                  </p>
                  
                  {scrapeStatus === 'loading' && (
                    <p className="text-sm text-blue-500 mt-1">
                      Scraping website... This may take a moment.
                    </p>
                  )}
                  {scrapeStatus === 'error' && (
                    <p className="text-sm text-red-500 mt-1">
                      Error scraping website. Please check the URL and try again.
                    </p>
                  )}
                </div>
              )}

              {activeTab === 'api' && (
                <div className="space-y-4">
                  <div className="form-group">
                    <label className="form-label">API Configuration</label>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label text-sm">HTTP Method</label>
                        <select
                          value={apiConfig.method}
                          onChange={(e) => setApiConfig(prev => ({ ...prev, method: e.target.value }))}
                          className="form-input"
                          disabled={hasReadOnlyAccess}
                        >
                          <option value="GET">GET</option>
                          <option value="POST">POST</option>
                          <option value="PUT">PUT</option>
                          <option value="PATCH">PATCH</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="form-label text-sm">API URL</label>
                        <input
                          type="url"
                          value={apiConfig.url}
                          onChange={(e) => setApiConfig(prev => ({ ...prev, url: e.target.value }))}
                          className="form-input"
                          placeholder="https://api.example.com/data"
                          disabled={hasReadOnlyAccess}
                          required
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="form-label text-sm">Headers (JSON format)</label>
                      <textarea
                        value={JSON.stringify(apiConfig.headers, null, 2)}
                        onChange={(e) => {
                          try {
                            const headers = JSON.parse(e.target.value);
                            setApiConfig(prev => ({ ...prev, headers }));
                          } catch (err) {
                            // Keep the text as-is if invalid JSON
                          }
                        }}
                        className="form-textarea h-100"
                        placeholder='{"Authorization": "Bearer your-token", "Content-Type": "application/json"}'
                        disabled={hasReadOnlyAccess}
                      />
                    </div>

                    {(apiConfig.method === 'POST' || apiConfig.method === 'PUT' || apiConfig.method === 'PATCH') && (
                      <div className="mt-4">
                        <label className="form-label text-sm">Request Body (JSON format)</label>
                        <textarea
                          value={apiConfig.body}
                          onChange={(e) => setApiConfig(prev => ({ ...prev, body: e.target.value }))}
                          className="form-textarea h-100"
                          placeholder='{"key": "value"}'
                          disabled={hasReadOnlyAccess}
                        />
                      </div>
                    )}

                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={handleTestApi}
                        disabled={apiTesting || hasReadOnlyAccess}
                        className="btn btn-secondary text-smd"
                      >
                        {apiTesting ? 'Testing API...' : 'Test API'}
                      </button>
                    </div>
                  </div>

                  {apiResponse && (
                    <div className="form-group">
                      <div className="flex items-center justify-between mb-4">
                        <label className="form-label">Data Selection Method</label>
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              checked={!useTemplateMode}
                              onChange={() => setUseTemplateMode(false)}
                              className="form-radio mr-2"
                              disabled={hasReadOnlyAccess}
                            />
                            Field Selection
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              checked={useTemplateMode}
                              onChange={() => setUseTemplateMode(true)}
                              className="form-radio mr-2"
                              disabled={hasReadOnlyAccess}
                            />
                            Template Mode
                          </label>
                        </div>
                      </div>

                      {!useTemplateMode ? (
                        <div>
                          <label className="form-label">API Response - Select Fields to Store</label>
                          <div className="border rounded p-4 max-h-400 overflow-y-auto bg-gray-50">
                            <p className="text-sm text-gray-600 mb-2">
                              Check the fields you want to include in the knowledge base document:
                            </p>
                            {renderJsonStructure(apiResponse)}
                          </div>
                          
                          {Object.keys(selectedFields).filter(path => selectedFields[path]).length > 0 && (
                            <div className="mt-4 p-3 bg-blue-50 rounded">
                              <h4 className="font-medium text-sm mb-2">Selected Fields:</h4>
                              <ul className="text-sm space-y-1">
                                {Object.keys(selectedFields)
                                  .filter(path => selectedFields[path])
                                  .map(path => (
                                    <li key={path} className="flex justify-between">
                                      <span className="font-mono">{path}</span>
                                      <span>→ {fieldMappings[path] || path.split('.').pop()}</span>
                                    </li>
                                  ))
                                }
                              </ul>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <label className="form-label">Data Formatting Template</label>
                          <p className="text-sm text-gray-600 mb-2">
                            Create a template to format the API data. This template will be processed and the result will be saved to the knowledge base.
                          </p>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div>
                              <textarea
                                value={contentTemplate}
                                onChange={(e) => setContentTemplate(e.target.value)}
                                className="form-textarea h-200"
                                placeholder="Example template:
{{data[*]|{#}. Name: {attributes.name}
ID: {id}
Status: {[if:active:equals:true:Active:Inactive]}

}}

Total: {{data.length}} items

Note: 'data' refers to the array in your API response. If your API returns {data: [...]}, the array will be available as {{data[*]|...}}"
                                disabled={hasReadOnlyAccess}
                              />
                              <div className="mt-2 text-xs text-gray-500">
                                <strong>Template Syntax:</strong><br/>
                                • <code>{'{{data[*]|template}}'}</code> - Iterate through data array<br/>
                                • <code>{'{{response.items[*]|template}}'}</code> - If API returns {`{items: [...]}`}<br/>
                                • <code>{'{field.nested}'}</code> - Access nested field in each item<br/>
                                • <code>{'{#}'}</code> - Item number (1, 2, 3...)<br/>
                                • <code>{'{[if:field:equals:value:then:else]}'}</code> - Conditionals<br/>
                                • <code>{'{{field}}'}</code> - Single field from response<br/>
                                • <code>{'{{data.length}}'}</code> - Array length<br/>
                                <strong>Your API structure:</strong> Test API first to see available fields
                              </div>
                            </div>
                            
                            <div>
                              <label className="form-label text-sm">
                                Formatted Preview 
                                {apiResponse?.formattedPreview && (
                                  <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                    Live Preview
                                  </span>
                                )}
                              </label>
                              <div className="border rounded p-3 bg-gray-50 h-200 overflow-y-auto">
                                <pre className="text-sm whitespace-pre-wrap">
                                  {generateTemplatePreview()}
                                </pre>
                              </div>
                              <div className="mt-2 text-xs text-blue-600">
                                💾 This formatted content will be saved to your knowledge base.
                                {apiResponse?.formattedPreview && (
                                  <div className="text-green-600 mt-1">
                                    ✅ Preview shows actual processed result from your API data
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedDocument && isApiDocument(selectedDocument) && (
                    <div className="form-group">
                      <div className="bg-blue-50 border border-blue-200 rounded p-4">
                        <h4 className="font-medium text-blue-800 mb-2">Re-scrape API Data</h4>
                        <p className="text-sm text-blue-700 mb-3">
                          This document was created from API scraping. You can update the API configuration and re-scrape to get fresh data.
                        </p>
                        <button
                          type="button"
                          onClick={handleRescrape}
                          disabled={isRescraping || hasReadOnlyAccess || !apiConfig.url}
                          className="btn btn-primary text-smd"
                        >
                          {isRescraping ? 'Re-scraping...' : 'Re-scrape Data'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Tags (comma-separated)</label>
                <input
                  type="text"
                  name="tags"
                  className="form-input"
                  defaultValue={selectedDocument?.tags?.join(', ')}
                  placeholder="e.g., faq, product, support"
                  disabled={hasReadOnlyAccess}
                />
              </div>

              <div className="flex gap-2">
                {canManageKnowledgeBase && (
                  <button 
                    type="submit" 
                    className="btn btn-primary text-smd"
                    disabled={isUploading || scrapeStatus === 'loading'}
                  >
                    {selectedDocument ? 'Update Document' : 'Create Document'}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary text-smd ml-2"
                  onClick={resetForm}
                >
                  {canManageKnowledgeBase ? 'Cancel' : 'Close'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="card mt-4">
          <h2 className="text-lg font-medium mb-4">Documents</h2>
          {isLoading ? (
            <p>Loading documents...</p>
          ) : documents?.length > 0 ? (
            <div className="space-y-4">
              {documents.map((document) => (
                <div 
                  key={document._id} 
                  ref={document._id === newlyAddedDocId ? newDocRef : null}
                  className={`border rounded p-4 transition-all duration-500 ${document._id === newlyAddedDocId ? 'bg-blue-50 border-blue-300 shadow-md' : ''}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium">{document.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {document.content.substring(0, 200)}...
                      </p>
                      {document.tags?.length > 0 && (
                        <div className="mt-2 space-x-2">
                          {document.tags.map((tag) => (
                            <span key={tag} className="badge badge-secondary">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      {document.source && (
                        <p className="text-xs text-gray-500 mt-1">
                          Source: {document.source}
                        </p>
                      )}
                    </div>
                    <div className="space-x-2">
                      {canManageKnowledgeBase && (
                        <>
                          <button
                            className="btn btn-sm btn-secondary text-smd mb-2"
                            style={{width: '100px'}}
                            onClick={() => {
                              setSelectedDocument(document);
                              setIsFormOpen(true);
                              if (isApiDocument(document)) {
                                setActiveTab('api');
                                loadApiDocumentData(document);
                              }
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger text-smd"
                            style={{width: '100px'}}
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this document?')) {
                                deleteDocument(document._id);
                              }
                            }}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No documents in the knowledge base yet. Add one to get started!</p>
          )}
        </div>
      </div>
      
      {/* Toast container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Layout>
  );
};

export default KnowledgeBase; 